import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import {
  ShoppingCart, TrendingUp, Users, Package, BarChart3,
  CalendarDays, Trophy, Smartphone, Coins, AlertTriangle, Percent,
} from 'lucide-react';
import {
  getCustomerOrderSourceLabel,
  getCustomerOrderStatusLabel,
} from '@/lib/crmApi';
import {
  fmt, fmtMoney, fmtPct, monthLabel, SummaryCard, HorizontalBar,
  MonthlyChart, TopProductsList,
} from '@/components/stats/StatsShared';
import {
  ProfitMethodologyNote,
  SummaryMetricBreakdown,
  ProductProfitBreakdown,
  ClientProfitBreakdown,
  SourceProfitBreakdown,
  MonthProfitBreakdown,
  SectionCriteriaNote,
  MarkupMarginHint,
} from '@/components/stats/ProfitabilityBreakdown';

const SOURCE_COLORS = {
  whatsapp: 'bg-green-100 text-green-700',
  store: 'bg-blue-100 text-blue-700',
  phone: 'bg-yellow-100 text-yellow-700',
  catalog: 'bg-purple-100 text-purple-700',
  manual: 'bg-gray-100 text-gray-700',
  other: 'bg-slate-100 text-slate-700',
};

export default function CustomerStatsPanel({ data }) {
  if (!data) return null;

  const {
    summary, status_distribution, by_source,
    top_products_by_qty, top_products_by_revenue,
    top_clients, monthly_revenue, profitability,
  } = data;

  const profitSummary = profitability?.summary;
  const hasProfitData = profitSummary && profitSummary.items_with_cost > 0;

  const maxStatusCount = Math.max(...(status_distribution || []).map((s) => s.count), 1);
  const maxSourceOrders = Math.max(...(by_source || []).map((s) => s.orders), 1);
  const maxProfit = Math.max(...(profitability?.top_products_by_profit || []).map((p) => p.profit), 1);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <SummaryCard icon={ShoppingCart} label="Заказов покупателей" value={fmt(summary.total_orders)} color="bg-blue-500" />
        <SummaryCard icon={TrendingUp} label="Выручка" value={fmtMoney(summary.total_revenue)} color="bg-green-500" />
        <SummaryCard icon={BarChart3} label="Средний чек" value={fmtMoney(summary.avg_order)} color="bg-indigo-500" />
        <SummaryCard icon={Users} label="Активных клиентов" value={fmt(summary.active_clients)} color="bg-purple-500" />
        <SummaryCard icon={Package} label="Товаров продано" value={fmt(summary.total_items_sold)} color="bg-orange-500" />
      </div>

      <Tabs defaultValue="products">
        <TabsList className="mb-4 flex-wrap h-auto">
          <TabsTrigger value="products"><Trophy className="w-4 h-4 mr-1.5" />Топ товаров</TabsTrigger>
          <TabsTrigger value="clients"><Users className="w-4 h-4 mr-1.5" />Клиенты</TabsTrigger>
          <TabsTrigger value="sources"><Smartphone className="w-4 h-4 mr-1.5" />Источники</TabsTrigger>
          <TabsTrigger value="statuses"><BarChart3 className="w-4 h-4 mr-1.5" />Статусы</TabsTrigger>
          <TabsTrigger value="dynamics"><CalendarDays className="w-4 h-4 mr-1.5" />Динамика</TabsTrigger>
          <TabsTrigger value="profit"><Coins className="w-4 h-4 mr-1.5" />Прибыльность</TabsTrigger>
        </TabsList>

        <TabsContent value="products">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold text-gray-800">Чаще всего покупают (по кол-ву)</CardTitle>
              </CardHeader>
              <CardContent>
                <TopProductsList
                  items={top_products_by_qty}
                  valueKey="total_qty"
                  valueLabel={(p) => `${fmt(p.total_qty)} шт`}
                  barColor="bg-blue-500"
                />
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold text-gray-800">Топ по выручке</CardTitle>
              </CardHeader>
              <CardContent>
                <TopProductsList
                  items={top_products_by_revenue}
                  valueKey="total_amount"
                  valueLabel={(p) => fmtMoney(p.total_amount)}
                  barColor="bg-green-500"
                />
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="clients">
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold text-gray-800">Топ клиентов по сумме заказов</CardTitle>
            </CardHeader>
            <CardContent>
              {(top_clients || []).length === 0 && <p className="text-gray-400 text-sm">Нет данных</p>}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th className="text-left py-2 text-gray-500 font-medium">#</th>
                      <th className="text-left py-2 text-gray-500 font-medium">Клиент</th>
                      <th className="text-right py-2 text-gray-500 font-medium">Заказов</th>
                      <th className="text-right py-2 text-gray-500 font-medium">Сумма</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(top_clients || []).map((c, i) => {
                      const maxAmt = top_clients[0]?.total_amount || 1;
                      return (
                        <React.Fragment key={c.client_id || i}>
                          <tr className="border-b border-gray-50">
                            <td className="py-2 pr-3">
                              <span className={`text-xs font-bold ${i < 3 ? 'text-yellow-500' : 'text-gray-400'}`}>{i + 1}</span>
                            </td>
                            <td className="py-2">
                              <Link to={`/crm/clients/${c.client_id}`} className="font-medium text-gray-900 hover:text-blue-600">
                                {c.client_name}
                              </Link>
                              {c.phone && <p className="text-xs text-gray-400">{c.phone}</p>}
                            </td>
                            <td className="py-2 text-right text-gray-600">{fmt(c.orders_count)}</td>
                            <td className="py-2 text-right font-semibold text-green-700">{fmtMoney(c.total_amount)}</td>
                          </tr>
                          <tr>
                            <td />
                            <td colSpan={3} className="pb-2">
                              <HorizontalBar value={c.total_amount} max={maxAmt} color="bg-purple-500" />
                            </td>
                          </tr>
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="sources">
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold text-gray-800">Заказы по источникам</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {(by_source || []).length === 0 && <p className="text-gray-400 text-sm">Нет данных</p>}
              {(by_source || []).map((s, i) => (
                <div key={s.source || i}>
                  <div className="flex items-center justify-between mb-1">
                    <Badge className={SOURCE_COLORS[s.source] || 'bg-gray-100 text-gray-600'}>
                      {getCustomerOrderSourceLabel(s.source)}
                    </Badge>
                    <div className="flex gap-3 text-sm">
                      <span className="text-gray-500">{fmt(s.orders)} зак.</span>
                      <span className="font-semibold text-green-700">{fmtMoney(s.amount)}</span>
                    </div>
                  </div>
                  <HorizontalBar value={s.orders} max={maxSourceOrders} color="bg-cyan-500" />
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="statuses">
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold text-gray-800">Распределение по статусам</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {(status_distribution || []).map((s, i) => (
                <div key={s.status || i}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-gray-800">
                      {s.label || getCustomerOrderStatusLabel(s.status)}
                    </span>
                    <div className="flex gap-3 text-sm">
                      <span className="text-gray-500">{fmt(s.count)} шт</span>
                      <span className="font-semibold text-gray-700">{fmtMoney(s.amount)}</span>
                    </div>
                  </div>
                  <HorizontalBar value={s.count} max={maxStatusCount} color="bg-violet-500" />
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="dynamics">
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-1">
              <CardTitle className="text-base font-semibold text-gray-800">Выручка по месяцам</CardTitle>
              <p className="text-xs text-gray-400">Последние 12 месяцев</p>
            </CardHeader>
            <CardContent>
              <MonthlyChart data={monthly_revenue} valueKey="amount" label="amount" color="#22c55e" />
              {monthly_revenue?.length > 0 && (
                <div className="mt-3 overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-gray-100">
                        <th className="text-left py-1 text-gray-400 font-medium">Месяц</th>
                        <th className="text-right py-1 text-gray-400 font-medium">Заказов</th>
                        <th className="text-right py-1 text-gray-400 font-medium">Сумма</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...monthly_revenue].reverse().map((m) => (
                        <tr key={m.month} className="border-b border-gray-50">
                          <td className="py-1 text-gray-700">{monthLabel(m.month)}</td>
                          <td className="py-1 text-right text-gray-600">{fmt(m.orders)}</td>
                          <td className="py-1 text-right font-medium text-green-700">{fmtMoney(m.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="profit">
          {!hasProfitData ? (
            <Card className="border-0 shadow-sm">
              <CardContent className="py-12 text-center">
                <p className="text-gray-500 text-sm">
                  Нет данных для расчёта прибыли — в позициях заказов не сохранена закупочная цена.
                </p>
                <p className="text-xs text-gray-400 mt-2">
                  Обновите цены из каталога в заказах, чтобы появился снимок закупочной цены.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              <ProfitMethodologyNote rubToKztRate={profitSummary.rub_to_kzt_rate} />

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <SummaryCard
                  icon={TrendingUp}
                  label="Выручка (с закупкой)"
                  value={fmtMoney(profitSummary.revenue_with_cost)}
                  sub={`${fmt(profitSummary.items_with_cost)} из ${fmt(profitSummary.items_total)} позиций`}
                  color="bg-green-500"
                  footer={<SummaryMetricBreakdown metric="revenue" data={profitSummary} />}
                />
                <SummaryCard
                  icon={Package}
                  label="Себестоимость"
                  value={fmtMoney(profitSummary.total_cost)}
                  color="bg-orange-500"
                  footer={<SummaryMetricBreakdown metric="cost" data={profitSummary} />}
                />
                <SummaryCard
                  icon={Coins}
                  label="Валовая прибыль"
                  value={fmtMoney(profitSummary.gross_profit)}
                  color="bg-emerald-500"
                  footer={<SummaryMetricBreakdown metric="profit" data={profitSummary} />}
                />
                <SummaryCard
                  icon={Percent}
                  label="Наценка от закупки"
                  value={`+${fmtPct(profitSummary.markup_percent)}`}
                  sub={`Маржа от продажи: ${fmtPct(profitSummary.margin_percent)} · покрытие закупкой: ${fmtPct(profitSummary.cost_coverage_percent)}`}
                  color="bg-indigo-500"
                  footer={<SummaryMetricBreakdown metric="margin" data={profitSummary} />}
                />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="border-0 shadow-sm">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base font-semibold text-gray-800">Топ товаров по прибыли</CardTitle>
                    <SectionCriteriaNote>
                      Сортировка по суммарной прибыли (выручка − себестоимость) по всем заказам
                    </SectionCriteriaNote>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {(profitability.top_products_by_profit || []).map((p, i) => (
                      <div key={p.product_id || i}>
                        <div className="flex items-start justify-between mb-1 gap-2">
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-gray-900 truncate">{p.product_name}</p>
                            <p className="text-xs text-gray-400">
                              {p.product_code} · {fmt(p.total_qty)} шт ·{' '}
                              <MarkupMarginHint markup={p.markup_percent} margin={p.margin_percent} />
                            </p>
                            <ProductProfitBreakdown product={p} compact />
                          </div>
                          <span className="text-sm font-semibold text-emerald-700 shrink-0">{fmtMoney(p.profit)}</span>
                        </div>
                        <HorizontalBar value={p.profit} max={maxProfit} color="bg-emerald-500" />
                      </div>
                    ))}
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-sm">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base font-semibold text-gray-800 flex items-center gap-2">
                      <Percent className="w-4 h-4" /> Лучшая наценка от закупки
                    </CardTitle>
                    <SectionCriteriaNote>
                      Товары с выручкой от 3 000 ₸ и продано ≥ 2 шт, сортировка по % наценки к закупу
                    </SectionCriteriaNote>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {(profitability.top_products_by_margin || []).length === 0 ? (
                      <p className="text-gray-400 text-sm">Недостаточно данных</p>
                    ) : (
                      (profitability.top_products_by_margin || []).map((p) => (
                        <div key={p.product_id} className="border-b border-gray-50 pb-3 last:border-0">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-gray-900 truncate">{p.product_name}</p>
                              <p className="text-xs text-gray-400">{p.product_code}</p>
                              <ProductProfitBreakdown product={p} compact />
                            </div>
                            <div className="text-right shrink-0">
                              <p className="font-semibold text-indigo-700">+{fmtPct(p.markup_percent)}</p>
                              <p className="text-sm text-emerald-700">{fmtMoney(p.profit)}</p>
                              <p className="text-xs text-gray-400">маржа {fmtPct(p.margin_percent)}</p>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </CardContent>
                </Card>
              </div>

              {(profitability.low_margin_products || []).length > 0 && (
                <Card className="border-0 shadow-sm border-l-4 border-l-amber-400">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base font-semibold text-gray-800 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-amber-500" />
                      Низкая наценка — стоит пересмотреть цену
                    </CardTitle>
                    <SectionCriteriaNote>
                      Наценка к закупу ниже 35% при выручке от 5 000 ₸ — мало зарабатываете относительно закупочной цены
                    </SectionCriteriaNote>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {profitability.low_margin_products.map((p) => (
                      <div key={p.product_id} className="border-b border-gray-50 pb-3 last:border-0">
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-gray-900">{p.product_name}</p>
                            <p className="text-xs text-gray-400">{p.product_code} · {fmt(p.total_qty)} шт</p>
                            <div className="flex flex-wrap gap-x-4 gap-y-0 text-sm mt-1">
                              <span>Выручка: {fmtMoney(p.revenue)}</span>
                              <span className="text-gray-600">Себест.: {fmtMoney(p.cost)}</span>
                              <span className="text-indigo-700 font-semibold">+{fmtPct(p.markup_percent)}</span>
                              <span className="text-gray-400"> к закупу · </span>
                              <span className="text-amber-600">маржа {fmtPct(p.margin_percent)}</span>
                              <span className="text-emerald-700">Прибыль: {fmtMoney(p.profit)}</span>
                            </div>
                            <ProductProfitBreakdown product={p} compact />
                          </div>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="border-0 shadow-sm">
                  <CardHeader className="pb-1">
                    <CardTitle className="text-base font-semibold text-gray-800">Прибыль по месяцам</CardTitle>
                    <SectionCriteriaNote>
                      Агрегация позиций с закупочной ценой по месяцу создания заказа
                    </SectionCriteriaNote>
                  </CardHeader>
                  <CardContent>
                    <MonthlyChart
                      data={profitability.monthly_profit}
                      valueKey="profit"
                      label="amount"
                      color="#10b981"
                    />
                    {profitability.monthly_profit?.length > 0 && (
                      <div className="mt-3 overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="border-b border-gray-100">
                              <th className="text-left py-1 text-gray-400 font-medium">Месяц</th>
                              <th className="text-right py-1 text-gray-400 font-medium">Выручка</th>
                              <th className="text-right py-1 text-gray-400 font-medium">Себест.</th>
                              <th className="text-right py-1 text-gray-400 font-medium">Прибыль</th>
                              <th className="text-right py-1 text-gray-400 font-medium">Наценка</th>
                              <th className="text-right py-1 text-gray-400 font-medium">Маржа</th>
                            </tr>
                          </thead>
                          <tbody>
                            {[...profitability.monthly_profit].reverse().map((m) => (
                              <React.Fragment key={m.month}>
                                <tr className="border-b border-gray-50 align-top">
                                  <td className="py-1 text-gray-700">
                                    <div>{monthLabel(m.month)}</div>
                                    <MonthProfitBreakdown month={m} />
                                  </td>
                                  <td className="py-1 text-right text-gray-600">{fmtMoney(m.revenue)}</td>
                                  <td className="py-1 text-right text-orange-600">{fmtMoney(m.cost)}</td>
                                  <td className="py-1 text-right font-medium text-emerald-700">{fmtMoney(m.profit)}</td>
                                  <td className="py-1 text-right font-medium text-indigo-700">+{fmtPct(m.markup_percent)}</td>
                                  <td className="py-1 text-right text-gray-500">{fmtPct(m.margin_percent)}</td>
                                </tr>
                              </React.Fragment>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </CardContent>
                </Card>

                <div className="space-y-6">
                  <Card className="border-0 shadow-sm">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base font-semibold text-gray-800">Прибыль по источникам</CardTitle>
                      <SectionCriteriaNote>
                        Сумма прибыли по каналу привлечения заказа
                      </SectionCriteriaNote>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {(profitability.profit_by_source || []).map((s) => (
                        <div key={s.source} className="border-b border-gray-50 pb-3 last:border-0">
                          <div className="flex items-center justify-between text-sm">
                            <Badge className={SOURCE_COLORS[s.source] || 'bg-gray-100 text-gray-600'}>
                              {getCustomerOrderSourceLabel(s.source)}
                            </Badge>
                            <div className="text-right">
                              <p className="font-semibold text-emerald-700">{fmtMoney(s.profit)}</p>
                              <p className="text-xs text-gray-400">
                                +{fmtPct(s.markup_percent)} к закупу · маржа {fmtPct(s.margin_percent)}
                              </p>
                            </div>
                          </div>
                          <SourceProfitBreakdown source={s} />
                        </div>
                      ))}
                    </CardContent>
                  </Card>

                  <Card className="border-0 shadow-sm">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base font-semibold text-gray-800">Топ клиентов по прибыли</CardTitle>
                      <SectionCriteriaNote>
                        Клиенты с наибольшей суммарной валовой прибылью по их заказам
                      </SectionCriteriaNote>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {(profitability.top_clients_by_profit || []).map((c) => {
                          const maxClientProfit = profitability.top_clients_by_profit[0]?.profit || 1;
                          return (
                            <div key={c.client_id} className="border-b border-gray-50 pb-3 last:border-0">
                              <div className="flex items-center justify-between mb-1">
                                <div>
                                  <Link to={`/crm/clients/${c.client_id}`} className="text-sm font-medium text-gray-900 hover:text-blue-600">
                                    {c.client_name}
                                  </Link>
                                  <p className="text-xs text-gray-400">
                                    {fmt(c.orders_count)} зак. ·{' '}
                                    <MarkupMarginHint markup={c.markup_percent} margin={c.margin_percent} />
                                  </p>
                                  <ClientProfitBreakdown client={c} />
                                </div>
                                <span className="text-sm font-semibold text-emerald-700">{fmtMoney(c.profit)}</span>
                              </div>
                              <HorizontalBar value={c.profit} max={maxClientProfit} color="bg-emerald-500" />
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
